import type { SetStateAction } from "react"
import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"

import { Grid } from "@mui/material"
import { InputText, Select, Snackbar, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { useFormikContext } from "formik"

import { DataMappingTable } from "../../../components/tables/DataMappingTable"
import { KonanAPI } from "../../../services/KonanAPI"
import type { CustomFeatureRequest, baseErrorType } from "../../../types/custom/projects"
import type { TrainingDataUpload } from "../../../types/generated/api/TrainingDataUpload"
import type { AutoMlCreationFormikValues } from "../../Models/interfaces"
import type { DataMappingFlowProps } from "../interfaces"

/**
 * The component for data mapping view (Target column, file headers table)
 * @param onDataChange setter for the upper state (excluded columns and mapping arrays)
 * @param createdProjectId projectId
 * @param fileHeaders CSV file headers passed to the data mapping columns
 * @param formik Formik controller
 * @param errors Axios errors
 * @return {React.ReactElement}
 */
export function DataMappingFlow(props: Readonly<DataMappingFlowProps>): React.ReactElement {
  const { onDataChange, createdProjectId: projectId, fileHeaders, errors, isSchemaLoading, trainingDataId } = props

  const { t } = useTranslation("projects")

  const {
    values,
    errors: formikErrors,
    touched,
    setFieldValue,
    handleChange,
    handleBlur,
  } = useFormikContext<AutoMlCreationFormikValues>()

  const [mapping, setMapping] = useState<Array<CustomFeatureRequest>>([])

  const { data: fetchPredefinedTypesData } = useQuery<AxiosResponse>(
    ["predefined-types", projectId],
    () => KonanAPI.fetchAutomlPredefinedTypes({ projectId, type: "credit_scoring" }),
    {
      enabled: !!projectId,
    },
  )

  // fetch single training data
  const { data: trainingData } = useQuery<AxiosResponse<TrainingDataUpload>, AxiosError<baseErrorType>>(
    ["training-data", projectId, trainingDataId],
    () => KonanAPI.fetchSingleTrainingData(projectId, trainingDataId),
    {
      enabled: !!projectId && !!trainingDataId,
      refetchInterval: 3000,
    },
  )

  // updating the mapping whenever pre-defined types or target column input change
  useEffect(() => {
    if (values.targetColumnType === "none" || !values.targetColumnType) {
      setFieldValue("targetColumnType", "default")
    }
    if (values.target_column && fileHeaders && fileHeaders.length > 0) {
      for (const col of fileHeaders) {
        if (col === values.target_column) {
          // setTimeout to avoid autocomplete flickering
          setTimeout(() => {
            setMapping((prevMapping: SetStateAction<CustomFeatureRequest[]>) => {
              if ((prevMapping as CustomFeatureRequest[]).some((row: CustomFeatureRequest) => row.target === true)) {
                return (prevMapping as CustomFeatureRequest[]).map((row: CustomFeatureRequest) =>
                  row.target === true
                    ? {
                        name: values.target_column,
                        type:
                          values.targetColumnType === "none" || values.targetColumnType === "default"
                            ? null
                            : values.targetColumnType,
                        target: true,
                      }
                    : row,
                )
              } else {
                return [
                  ...(prevMapping as CustomFeatureRequest[]),
                  {
                    name: values.target_column,
                    target: true,
                    type:
                      values.targetColumnType === "none" || values.targetColumnType === "default"
                        ? null
                        : values.targetColumnType,
                  },
                ]
              }
            })
          }, 5)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileHeaders, values.targetColumnType, values.target_column])

  return (
    <Grid container item spacing={2} padding={0.5}>
      {/* TODO: refactor this error checking when we have error schema on endpoints from the BE */}
      {errors?.response?.data?.details && (
        <Grid item xs={12} mb={1}>
          <Snackbar severity="error" fullWidth description={errors?.response?.data?.details} />
        </Grid>
      )}
      {errors?.response?.data?.name && errors?.response?.data?.name[0] && (
        <Grid item xs={12} mb={1}>
          <Snackbar severity="error" fullWidth description={errors?.response?.data?.name[0]} />
        </Grid>
      )}
      {errors?.response?.data?.error && (
        <Grid item xs={12} mb={1}>
          <Snackbar severity="error" fullWidth description={errors?.response?.data?.error} />
        </Grid>
      )}
      {errors?.response?.data?.mapping && (
        <Grid item xs={12} mb={1}>
          <Snackbar severity="error" fullWidth description={t("dataMapping.errors.targetColumnType")} />
        </Grid>
      )}
      {errors?.response?.data?.target_column && (
        <Grid item xs={12} mb={1}>
          <Snackbar severity="error" fullWidth description={errors?.response?.data?.target_column[0]} />
        </Grid>
      )}
      {errors?.response?.data?.excluded_columns && (
        <Grid item xs={12} mb={1}>
          <Snackbar severity="error" fullWidth description={errors?.response?.data?.excluded_columns[0]} />
        </Grid>
      )}
      {(trainingData?.data?.status === "pending" || trainingData?.data?.status === "processing") && (
        <Grid item xs={12} mb={1}>
          <Snackbar
            severity="warning"
            fullWidth
            description={t("dataMapping.warnings.trainingDataProcessing", { status: trainingData.data?.status })}
          />
        </Grid>
      )}
      {trainingData?.data?.status === "error" && (
        <Grid item xs={12} mb={1}>
          <Snackbar severity="error" fullWidth description={t("dataMapping.errors.trainingDataFailed")} />
        </Grid>
      )}

      <Grid item>
        <Typography variant="h3-bold">{t("dataMapping.title")}</Typography>
      </Grid>

      <Grid item xs={12} justifyContent="space-between" container spacing={1}>
        <Grid item xs={6}>
          <InputText
            label={t("dataMapping.fields.targetColumn.label")}
            id="target_column"
            placeholder={t("dataMapping.fields.targetColumn.placeholder")}
            variant="filled"
            hideDescription
            required
            value={values.target_column}
            handleChange={handleChange}
            handleBlur={handleBlur}
            error={touched.target_column && Boolean(formikErrors.target_column) && formikErrors.target_column}
            fullWidth
            options={fileHeaders ?? []}
            key={fileHeaders ? fileHeaders[0] : ""}
          />
        </Grid>
        <Grid item xs={6}>
          <Select
            label={t("dataMapping.fields.type.label")}
            fullWidth
            id="targetColumnType"
            hideDescription
            value={values.targetColumnType || "default"}
            handleChange={handleChange}
            optionsWithValues={[
              { label: t("dataMapping.fields.type.options.chooseType"), value: "default", disabled: true },
              { label: t("dataMapping.fields.type.options.chooseNone"), value: "none" },
              //  eslint-disable-next-line @typescript-eslint/no-explicit-any
              ...(fetchPredefinedTypesData?.data?.results?.map((item: any) => {
                return { label: item.name, value: item.name }
              }) ?? []),
            ]}
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <DataMappingTable
          data={fetchPredefinedTypesData?.data?.results ?? []}
          onDataChange={onDataChange}
          fileHeaders={fileHeaders}
          isSchemaLoading={isSchemaLoading}
          targetColumn={values.target_column}
          targetColumnType={values.targetColumnType}
          mapping={mapping}
          setMapping={setMapping}
        />
      </Grid>
    </Grid>
  )
}
